import { useState } from 'react';
import { ArrowLeft, Edit, Code2, Table, Download, Copy, Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { DimensionSchemaConfig } from '../types/dimension.types';
import { generateCompleteSchema, getCategoryIcon } from './dimension-utils';

interface DimensionPreviewProps {
  dimension: DimensionSchemaConfig;
  onEdit: () => void;
  onBack: () => void;
}

export function DimensionPreview({ dimension, onEdit, onBack }: DimensionPreviewProps) {
  const [copied, setCopied] = useState<string | null>(null);
  const generatedSchema = generateCompleteSchema(dimension);

  const categoryIcon = getCategoryIcon(dimension.category);

  const handleCopy = (text: string, type: string) => {
    navigator.clipboard.writeText(text);
    setCopied(type);
    setTimeout(() => setCopied(null), 2000);
  };

  const handleDownload = (filename: string, content: string) => {
    const blob = new Blob([content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  };

  const requiredFieldsCount = dimension.fields.filter(f => f.required).length;
  const indexedFieldsCount = dimension.fields.filter(f => f.indexed).length;
  const uniqueFieldsCount = dimension.fields.filter(f => f.unique).length;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={onBack}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div className="flex items-center gap-3">
            {categoryIcon}
            <div>
              <h1 className="text-2xl font-bold">{dimension.name}</h1>
              <p className="text-muted-foreground text-sm">
                {dimension.tableName || `dim_${dimension.id}`}
              </p>
            </div>
          </div>
        </div>
        <Button onClick={onEdit}>
          <Edit className="h-4 w-4 mr-2" />
          Edit
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-5 gap-4">
        <Card>
          <CardContent className="pt-4">
            <div className="text-2xl font-bold">{dimension.fields.length}</div>
            <p className="text-xs text-muted-foreground">Total Fields</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="text-2xl font-bold">{requiredFieldsCount}</div>
            <p className="text-xs text-muted-foreground">Required</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="text-2xl font-bold">{indexedFieldsCount}</div>
            <p className="text-xs text-muted-foreground">Indexed</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="text-2xl font-bold">{uniqueFieldsCount}</div>
            <p className="text-xs text-muted-foreground">Unique</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="text-2xl font-bold capitalize">{dimension.category}</div>
            <p className="text-xs text-muted-foreground">Category</p>
          </CardContent>
        </Card>
      </div>

      {/* Description */}
      {dimension.description && (
        <Card>
          <CardContent className="pt-4">
            <p className="text-muted-foreground">{dimension.description}</p>
          </CardContent>
        </Card>
      )}

      {/* Main Content */}
      <Tabs defaultValue="fields" className="space-y-4">
        <TabsList>
          <TabsTrigger value="fields">
            <Table className="h-4 w-4 mr-2" />
            Fields
          </TabsTrigger>
          <TabsTrigger value="prisma">
            <Code2 className="h-4 w-4 mr-2" />
            Prisma Schema
          </TabsTrigger>
          <TabsTrigger value="sql">
            <Code2 className="h-4 w-4 mr-2" />
            SQL Schema
          </TabsTrigger>
        </TabsList>

        {/* Fields Tab */}
        <TabsContent value="fields" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Field Definitions</CardTitle>
              <CardDescription>
                Detailed configuration of each field in this dimension
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-2 px-4 font-medium">Field Name</th>
                      <th className="text-left py-2 px-4 font-medium">Type</th>
                      <th className="text-left py-2 px-4 font-medium">Required</th>
                      <th className="text-left py-2 px-4 font-medium">Unique</th>
                      <th className="text-left py-2 px-4 font-medium">Indexed</th>
                      <th className="text-left py-2 px-4 font-medium">Description</th>
                    </tr>
                  </thead>
                  <tbody>
                    {dimension.fields.map((field, index) => (
                      <tr key={index} className="border-b hover:bg-muted/50">
                        <td className="py-3 px-4">
                          <div>
                            <p className="font-medium">{field.name}</p>
                            {field.label && field.label !== field.name && (
                              <p className="text-xs text-muted-foreground">{field.label}</p>
                            )}
                          </div>
                        </td>
                        <td className="py-3 px-4">
                          <Badge variant="outline">{field.type}</Badge>
                          {field.length && <span className="text-xs text-muted-foreground ml-2">({field.length})</span>}
                          {field.precision && <span className="text-xs text-muted-foreground ml-2">({field.precision},{field.scale})</span>}
                        </td>
                        <td className="py-3 px-4">
                          {field.required ? <Badge variant="default">Yes</Badge> : <span className="text-muted-foreground">No</span>}
                        </td>
                        <td className="py-3 px-4">
                          {field.unique ? <Badge variant="secondary">Yes</Badge> : <span className="text-muted-foreground">No</span>}
                        </td>
                        <td className="py-3 px-4">
                          {field.indexed ? <Badge>Yes</Badge> : <span className="text-muted-foreground">No</span>}
                        </td>
                        <td className="py-3 px-4 text-sm text-muted-foreground">
                          {field.description || '-'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Prisma Schema Tab */}
        <TabsContent value="prisma" className="space-y-4">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Prisma Schema</CardTitle>
                  <CardDescription>
                    Copy this to your Prisma schema file
                  </CardDescription>
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleCopy(generatedSchema.prismaSchema, 'prisma')}
                  >
                    {copied === 'prisma' ? <Check className="h-4 w-4 mr-2" /> : <Copy className="h-4 w-4 mr-2" />}
                    {copied === 'prisma' ? 'Copied!' : 'Copy'}
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleDownload(`${dimension.id}.prisma`, generatedSchema.prismaSchema)}
                  >
                    <Download className="h-4 w-4 mr-2" />
                    Download
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <pre className="bg-muted p-4 rounded-lg overflow-auto text-sm max-h-96">
                {generatedSchema.prismaSchema}
              </pre>
            </CardContent>
          </Card>
        </TabsContent>

        {/* SQL Schema Tab */}
        <TabsContent value="sql" className="space-y-4">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>SQL CREATE TABLE</CardTitle>
                  <CardDescription>
                    PostgreSQL table creation script
                  </CardDescription>
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleCopy(generatedSchema.createTableSQL, 'sql')}
                  >
                    {copied === 'sql' ? <Check className="h-4 w-4 mr-2" /> : <Copy className="h-4 w-4 mr-2" />}
                    {copied === 'sql' ? 'Copied!' : 'Copy'}
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleDownload(`${dimension.id}.sql`, generatedSchema.createTableSQL)}
                  >
                    <Download className="h-4 w-4 mr-2" />
                    Download
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <pre className="bg-muted p-4 rounded-lg overflow-auto text-sm max-h-96">
                {generatedSchema.createTableSQL}
              </pre>
            </CardContent>
          </Card>

          {generatedSchema.createIndexSQL.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle>Constraints & Indexes</CardTitle>
                <CardDescription>
                  Additional constraints and indexes for performance and data integrity
                </CardDescription>
              </CardHeader>
              <CardContent>
                <pre className="bg-muted p-4 rounded-lg overflow-auto text-sm max-h-64">
                  {generatedSchema.createIndexSQL.join('\n')}
                </pre>
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>

      {/* Government-specific Info */}
      {dimension.category === 'government' && (
        <Card className="border-blue-200 bg-blue-50/50">
          <CardHeader>
            <CardTitle className="text-blue-900">Government Data Standards</CardTitle>
            <CardDescription>
              This dimension follows Indonesian government data standards
            </CardDescription>
          </CardHeader>
          <CardContent className="text-sm text-blue-900">
            <ul className="list-disc list-inside space-y-1">
              <li>Uses BPS (Badan Pusat Statistik) codes for geographic data</li>
              <li>Follows RKPD/KUA-PPAS standards for budget/program data</li>
              <li>Compatible with SIPD-RI integration format</li>
              <li>Supports data exchange with SP4D and other government systems</li>
            </ul>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
